/**
 * Archetype World content configs.
 *
 * Each non-Curated archetype ("world") uses the same shape:
 *   - portals: 2 affiliate portal banners the user is applying to
 *   - inspirations: 8-10 hand-picked category tiles with Pexels imagery
 *
 * `outbound_url` values are '#' placeholders until affiliate approval lands.
 * Swap them in place once you have real affiliate URLs — that's the only
 * change needed to go from placeholder to live commerce.
 */

// Legacy exports kept for backwards compatibility with any external references.
// New code should import WORLDS or getArchetypeWorld().
export const EXPERIENCE_INSPIRATIONS = [];
export const EXPERIENCE_PORTALS = [];

export const WORLDS = {
  experiences: {
    inspirationHeading: 'Popular experience gifts',
    inspirationSubheading: 'Tap any category to browse experiences via our partners.',
    closingCta: {
      eyebrow: 'Location-based',
      title: 'Browse experiences near you',
      body: 'Our partners offer thousands of experiences filterable by city and region. Hop over to browse the full catalog.',
      button: 'Browse by location',
    },
    portals: [
      {
        id: 'giftory',
        name: 'Giftory',
        tagline: 'Curated experience gifts across the US',
        outbound_url: '#',
      },
      {
        id: 'wonderbox',
        name: 'Wonderbox',
        tagline: 'Boxed experience gift cards, redeemable nationwide',
        outbound_url: '#',
      },
    ],
    inspirations: [
      { id: 'skydiving', title: 'Skydiving', description: 'The ultimate adrenaline rush',
        image: 'https://images.pexels.com/photos/1198507/pexels-photo-1198507.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'hot-air-balloon', title: 'Hot Air Balloon', description: 'Sunrise flights above rolling landscapes',
        image: 'https://images.pexels.com/photos/2033343/pexels-photo-2033343.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'cooking-class', title: 'Cooking Class', description: 'Hands-on lessons with local chefs',
        image: 'https://images.pexels.com/photos/2544829/pexels-photo-2544829.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'spa-day', title: 'Spa Day', description: 'Full-day retreats and treatments',
        image: 'https://images.pexels.com/photos/3757942/pexels-photo-3757942.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'wine-tasting', title: 'Wine Tasting', description: 'Vineyard tours and curated flights',
        image: 'https://images.pexels.com/photos/1123260/pexels-photo-1123260.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'escape-room', title: 'Escape Room', description: 'Puzzle adventures for groups',
        image: 'https://images.pexels.com/photos/6373305/pexels-photo-6373305.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'sailing', title: 'Sailing', description: 'Sunset cruises and coastal trips',
        image: 'https://images.pexels.com/photos/1007836/pexels-photo-1007836.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'ziplining', title: 'Ziplining', description: 'Treetop courses through forest canopies',
        image: 'https://images.pexels.com/photos/6032291/pexels-photo-6032291.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'pottery', title: 'Pottery Class', description: 'Wheel-throwing and hand-building sessions',
        image: 'https://images.pexels.com/photos/2162938/pexels-photo-2162938.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'brewery-tour', title: 'Brewery Tour', description: 'Behind-the-scenes with tastings',
        image: 'https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
    ],
  },

  handmade: {
    inspirationHeading: 'Handmade & personalized favourites',
    inspirationSubheading: 'Small-batch, artisan-made, and made-just-for-them — shop directly from independent studios.',
    closingCta: {
      eyebrow: 'Support artisans',
      title: 'Shop the full artisan catalog',
      body: 'Explore thousands of independent makers across our partners — every purchase supports a small studio.',
      button: 'Browse all handmade',
    },
    portals: [
      {
        id: 'etsy',
        name: 'Etsy',
        tagline: 'Millions of independent creators, one marketplace',
        outbound_url: '#',
      },
      {
        id: 'uncommongoods',
        name: 'UncommonGoods',
        tagline: 'Handmade, small-batch, and personalized — thoughtfully curated',
        outbound_url: '#',
      },
    ],
    inspirations: [
      { id: 'ceramics', title: 'Ceramics', description: 'Wheel-thrown mugs, vases, and bowls',
        image: 'https://images.pexels.com/photos/2162938/pexels-photo-2162938.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'candles', title: 'Candles', description: 'Hand-poured soy and beeswax candles',
        image: 'https://images.pexels.com/photos/1123262/pexels-photo-1123262.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'jewelry', title: 'Jewelry & Engraving', description: 'Personalized rings, necklaces, earrings',
        image: 'https://images.pexels.com/photos/1191531/pexels-photo-1191531.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'textiles', title: 'Textiles', description: 'Handwoven throws, tapestries, and rugs',
        image: 'https://images.pexels.com/photos/6580226/pexels-photo-6580226.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'woodwork', title: 'Woodwork', description: 'Turned bowls, cutting boards, small furniture',
        image: 'https://images.pexels.com/photos/3637739/pexels-photo-3637739.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'leather', title: 'Leather Goods', description: 'Monogrammed wallets, journals, bags',
        image: 'https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'soap', title: 'Soaps & Bath', description: 'Cold-process soaps and bath salts',
        image: 'https://images.pexels.com/photos/4041392/pexels-photo-4041392.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'portraits', title: 'Custom Portraits', description: 'Commissioned paintings & pet portraits',
        image: 'https://images.pexels.com/photos/1266808/pexels-photo-1266808.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'photo_books', title: 'Photo Books', description: 'Beautifully bound memory books',
        image: 'https://images.pexels.com/photos/1005644/pexels-photo-1005644.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'star_maps', title: 'Custom Star Maps', description: 'The night sky from a special date',
        image: 'https://images.pexels.com/photos/1252890/pexels-photo-1252890.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
    ],
  },

  diy_kits: {
    inspirationHeading: 'Kits worth making',
    inspirationSubheading: 'Everything they need to make something great — tools, materials, and instructions in one box.',
    closingCta: {
      eyebrow: 'For every skill level',
      title: 'Find their next project',
      body: 'From absolute-beginner kits to advanced crafts, our partners carry kits for every age and interest.',
      button: 'Browse all kits',
    },
    portals: [
      {
        id: 'kiwico',
        name: 'KiwiCo',
        tagline: 'Age-based STEAM and craft kits for kids and adults',
        outbound_url: '#',
      },
      {
        id: 'cratejoy',
        name: 'Cratejoy',
        tagline: 'Subscription kit boxes across every craft imaginable',
        outbound_url: '#',
      },
    ],
    inspirations: [
      { id: 'craft-kits', title: 'Craft Kits', description: 'Embroidery, paper craft, macramé',
        image: 'https://images.pexels.com/photos/6942719/pexels-photo-6942719.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'model-kits', title: 'Model Building', description: 'Wooden puzzles, model ships, kits',
        image: 'https://images.pexels.com/photos/3661193/pexels-photo-3661193.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'cooking-kits', title: 'Cooking Kits', description: 'Fresh-pasta, cheese-making, sushi kits',
        image: 'https://images.pexels.com/photos/1435904/pexels-photo-1435904.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'painting-kits', title: 'Painting Kits', description: 'Watercolor, acrylic, paint-by-number',
        image: 'https://images.pexels.com/photos/1266808/pexels-photo-1266808.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'terrarium', title: 'Terrarium Kits', description: 'Miniature glass gardens',
        image: 'https://images.pexels.com/photos/3076899/pexels-photo-3076899.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'candle-making', title: 'Candle Making', description: 'Soy wax, wicks, fragrance kits',
        image: 'https://images.pexels.com/photos/1123262/pexels-photo-1123262.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'baking-kits', title: 'Baking Kits', description: 'Sourdough, macaron, cake-decorating',
        image: 'https://images.pexels.com/photos/1070947/pexels-photo-1070947.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'science-kits', title: 'Science Kits', description: 'Chemistry, robotics, electronics',
        image: 'https://images.pexels.com/photos/5185076/pexels-photo-5185076.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'leather-kits', title: 'Leather Kits', description: 'Wallet, notebook, and belt kits',
        image: 'https://images.pexels.com/photos/1152077/pexels-photo-1152077.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
      { id: 'puzzle-kits', title: 'Puzzle Boxes', description: 'Escape-room-style puzzle sets',
        image: 'https://images.pexels.com/photos/3097129/pexels-photo-3097129.jpeg?auto=compress&cs=tinysrgb&w=800', outbound_url: '#' },
    ],
  },

  keepsakes: {
    // Deprecated: content merged into `handmade` (Feb 2026). Kept as an
    // empty stub so any lingering ?archetype=keepsakes URLs don't error —
    // they fall through to the "Coming Soon" placeholder in ArchetypeWorld.
    __deprecated: true,
    portals: [],
    inspirations: [],
  },
};

export function getArchetypeWorld(id) {
  const w = WORLDS[id];
  if (!w || w.__deprecated) return null;
  return w;
}
